import { useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./MobileProfile.css";
import { ThemeContext } from "../context/themeContext";
import { AvatarContext } from "../context/AvatarContext";
import { UserContext } from "../context/UserContext";
import { FaLock, FaPlus } from "react-icons/fa";
import PasswordRecoverPopup from "../auth/PasswordRecoverPopup";
import EditProfilePopup from "./EditProfilePopup";
import {
  FaPalette,
  FaFileContract,
  FaReceipt,
  FaSignOutAlt,
  FaSignInAlt,
  FaCog,
  FaMoon,
  FaMapMarkerAlt,
  FaPencilAlt,
  FaArrowLeft,
} from "react-icons/fa";

export default function MobileProfile({ isOpen, onClose }) {
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showPlus, setShowPlus] = useState(false);

  const fileInputRef = useRef(null);

  const { avatar, setAvatar, avatarColor } = useContext(AvatarContext);
  const { activeTheme, handleThemeChange } = useContext(ThemeContext);
  const { user, setUser } = useContext(UserContext);

  const navigate = useNavigate();
  const partA = crypto.randomUUID() + crypto.randomUUID();
  const partB = crypto.randomUUID() + crypto.randomUUID();

  if (!isOpen) return null;

  /* ================= LOGOUT ================= */
  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {}

    try {
      localStorage.removeItem("user");
      localStorage.removeItem("scorecardData");
      localStorage.removeItem("reportAccess");
      localStorage.removeItem("reportBAccess");
      localStorage.removeItem("scorecardAccess");
    } catch (e) {}

    setUser?.(null);
    onClose?.();
    navigate("/");
  };

  /* ================= AVATAR ================= */
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatar(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div
      className={`profile-dropdown-overlay ${activeTheme === "dark" ? "dark-mode" : ""}`}
      onClick={onClose}
    >
      <div
        className={`profile-dropdownm ${activeTheme === "dark" ? "dark-mode" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ================= BACK / CLOSE BUTTON ================= */}
        <div className="profile-topbar">
          <button className="profile-back-btn" onClick={onClose} aria-label="Close profile">
            <FaArrowLeft />
          </button>
          <span className="profile-topbar-title">Profile</span>
        </div>

        {/* ================= HEADER ================= */}
        <div className="profile-header">
          <div
            className="profile-avatar-mobile-mob"
            title="Upload profile picture"
            onClick={() => setShowPlus(true)}
            style={{ background: avatar ? "transparent" : avatarColor }}
          >
            {avatar ? (
              <img src={avatar} alt="Profile" className="profile-avatar-image-mobile" />
            ) : (
              <span className="avatar-letter-mobile">
                {user?.name?.trim()?.[0]?.toUpperCase() || "?"}
              </span>
            )}

            {showPlus && (
              <span
                className="avatar-plus-mobile"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAvatarClick();
                  setShowPlus(false);
                }}
              >
                <FaPlus />
              </span>
            )}
          </div>

          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <div className="profile-info-mobile">
            <div className="profile-name-wrapper-mobile">
              <h4 className="profile-name-mobile">
                {user?.name ? `${user.name} Pvt. Ltd.`.slice(0, 80) : ""}
              </h4>
              {/* Edit icon opens the same popup as desktop */}
              <FaPencilAlt
                className="edit-name-icon-mobile-view"
                title="Edit profile"
                onClick={() => setShowEditPopup(true)}
              />
            </div>
            <p className="profile-email-mobile">{user?.email || ""}</p>
          </div>
        </div>

        {/* ================= MENU ================= */}
        <ul className="profile-menu-mobile">
          <li
            className="theme-dropdown"
            onClick={() => setShowThemeDropdown(!showThemeDropdown)}
          >
            <FaPalette className="menu-icon" />
            <span>Theme</span>

            {showThemeDropdown && (
              <ul className="theme-options">
                <li onClick={() => { handleThemeChange("light"); onClose?.(); }}>
                  <FaCog className="theme-icon" /> Light
                </li>
                <li onClick={() => { handleThemeChange("dark"); onClose?.(); }}>
                  <FaMoon className="theme-icon" /> Dark
                </li>
              </ul>
            )}
          </li>

          <li onClick={() => setShowRecover(true)}>
            <FaLock className="menu-icon" />
            <span>Change Password</span>
          </li>

          <li>
            <span
              className="profile-link"
              onClick={() => { navigate("/privacy-policy"); onClose?.(); }}
              style={{ cursor: "pointer" }}
            >
              <FaFileContract className="menu-icon" />
              <span>Terms & Conditions</span>
            </span>
          </li>

          <li>
            <span
              className="profile-link"
              onClick={() => { navigate(`/orders/${partA}/${partB}`); onClose?.(); }}
              style={{ cursor: "pointer" }}
            >
              <FaReceipt className="menu-icon" />
              <span>Orders</span>
            </span>
          </li>

          <li>
            <span
              className="profile-link"
              onClick={() => { navigate(`/profile/addresses/${partA}/${partB}`); onClose?.(); }}
              style={{ cursor: "pointer" }}
            >
              <FaMapMarkerAlt className="menu-icon" />
              <span>Saved Addresses</span>
            </span>
          </li>
        </ul>

        {/* ================= LOGOUT / LOGIN ================= */}
        {user ? (
          <div className="logout-row-mobile" onClick={handleLogout}>
            <FaSignOutAlt className="logout-icon" />
            <div>Log out</div>
          </div>
        ) : (
          <div
            className="logout-row-mobile"
            style={{ color: "var(--color-brand-primary)" }}
            onClick={() => {
              window.dispatchEvent(new CustomEvent("show-guest-auth"));
              onClose?.();
            }}
          >
            <FaSignInAlt style={{ color: "var(--color-brand-primary)", width: 18 }} />
            <div>Log in</div>
          </div>
        )}
      </div>

      {/* ================= PASSWORD RECOVER ================= */}
      {showRecover && (
        <PasswordRecoverPopup onCloseAll={() => setShowRecover(false)} />
      )}

      {/* ================= EDIT PROFILE POPUP (same as desktop) ================= */}
      {showEditPopup && (
        <EditProfilePopup
          user={user}
          onClose={() => setShowEditPopup(false)}
          onSave={({ name, email }) => {
            setUser({ ...user, ...(name && { name }), ...(email && { email }) });
          }}
        />
      )}
    </div>
  );
}